// UI strings for the weekly shared session, Finnish (default) and English side by side.
import { defineArea } from '../area';

export const sharedFi = {
  title: 'Yhteinen sessio',
  week: (w: string) => `Viikko ${w.replace(/^\d{4}-W0?/, '')}`,
  empty: 'Tällä viikolla ei ole vielä tehtäviä. Ne valitaan avatuista oppitunneista.',
  start: (n: number) => `Aloita (${n} ${n === 1 ? 'tehtävä' : 'tehtävää'})`,
  needsOnline: 'Tuloksen lähettäminen vaatii yhteyden. Tee sessio, kun yhteys toimii.',
  sending: 'Lähetetään tulosta…',
  done: 'Olet tehnyt tämän viikon session.',
  waitingPartner: (name: string) => `${name} ei ole vielä tehnyt tämän viikon sessiota. Vastaukset näkyvät rinnakkain, kun hän on valmis.`,
  partnerDone: (name: string) => `${name} on jo tehnyt session. Tulokset näkyvät, kun sinäkin olet valmis.`,
  side: 'Tulokset rinnakkain',
  score: (pct: number) => `${pct}\u00a0%`,
  at: (date: string) => `tehty ${date}`,
  item: (n: number) => `Tehtävä ${n}`,
  answer: 'vastaus',
  noAnswer: '—',
  missing: (ref: string) => `puuttuu: ${ref}`,
  skipped: (n: number) =>
    n === 1
      ? '1 tehtävä jäi pois, koska sen sisältöä ei löytynyt tältä laitteelta.'
      : `${n} tehtävää jäi pois, koska niiden sisältöä ei löytynyt tältä laitteelta.`,
  waitingIndex: 'Odotetaan taivutusmuotojen hakemistoa…',
  lessonWeek: (n: number) => `Oppitunnin ${n} viikko`,
  expected: 'oikea vastaus',
  yourReview: 'Omat vastauksesi',
  savedLocally: 'Vastauksesi on tallessa tällä laitteella ja lähtee, kun yhteys palaa.',
  // ---- layout v2 (docs/design/SIVUPALSTA.md "Yhteinen sessio") ----
  /** The progress line while the tasks run: one task per screen. */
  taskOf: (i: number, n: number) => `Tehtävä ${i}/${n}`,
  tasks: 'Viikon tehtävät',
  /** The scene's row on the session page: how many lines have a take. */
  sceneTakes: (n: number, total: number) => `${n}/${total} repliikkiä äänitetty`,
  sceneNext: 'Seuraavaksi: kohtaus roolipelinä',
  /** A line's recordings (a note level with the line). */
  takes: 'Äänitykset',
  record: 'Äänitä',
  /** The shared record as sentences, one per person. */
  recordLine: (name: string, days: number, daysWord: string, steady: number, steadyWord: string) => `${name}: ${days} ${daysWord}, ${steady} ${steadyWord}`,
  // ---- phone fixes 2026-10-07: leaving the round asks first ----
  /** The progress line on a phone: the week and the task on one row. */
  weekTask: (week: string, task: string) => `${week} · ${task}`,
  leaveTitle: 'Keskeytetäänkö kierros?',
  leaveBody: 'Tämän kierroksen vastaukset eivät tallennu.',
  leaveStay: 'Jatka kierrosta',
  leaveGo: 'Keskeytä',
};

export const sharedEn: typeof sharedFi = {
  title: 'Shared session',
  week: (w) => `Week ${w.replace(/^\d{4}-W0?/, '')}`,
  empty: 'No tasks this week yet. They are chosen from unlocked lessons.',
  start: (n) => `Start (${n} ${n === 1 ? 'task' : 'tasks'})`,
  needsOnline: 'Sending the result needs a connection. Do the session when you are online.',
  sending: 'Sending the result…',
  done: 'You have done this week’s session.',
  waitingPartner: (name) => `${name} has not done this week’s session yet. The answers appear side by side once they are done.`,
  partnerDone: (name) => `${name} has already done the session. The results appear once you are done too.`,
  side: 'Results side by side',
  score: (pct) => `${pct}%`,
  at: (date) => `done ${date}`,
  item: (n) => `Task ${n}`,
  answer: 'answer',
  noAnswer: '—',
  missing: (ref) => `missing: ${ref}`,
  skipped: (n) => `${n} ${n === 1 ? 'task was' : 'tasks were'} left out because their material was not found on this device.`,
  waitingIndex: 'Waiting for the index of inflected forms…',
  lessonWeek: (n) => `Lesson ${n} week`,
  expected: 'right answer',
  yourReview: 'Your answers',
  savedLocally: 'Your answers are kept on this device and sent once you are back online.',
  taskOf: (i, n) => `Task ${i}/${n}`,
  tasks: 'This week’s tasks',
  sceneTakes: (n, total) => `${n}/${total} lines recorded`,
  sceneNext: 'Next: the scene as role play',
  takes: 'Recordings',
  record: 'Record',
  recordLine: (name, days, daysWord, steady, steadyWord) => `${name}: ${days} ${daysWord}, ${steady} ${steadyWord}`,
  weekTask: (week, task) => `${week} · ${task}`,
  leaveTitle: 'Stop the round?',
  leaveBody: 'The answers of this round will not be saved.',
  leaveStay: 'Keep going',
  leaveGo: 'Stop',
};

// Messages.shared (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('shared', sharedFi, sharedEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof sharedFi> {}
}
